import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { Chip } from '@crowdex/ui/components/chip';
import { FileBox, MousePointer2 } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * An artifact's header in the panel (D15): its name, the variants its view offers as pills (the
 * one shown filled), "Point at an element" when the view takes pointing, then its actions.
 */
export function ArtifactPanelHeader({
  artifact,
  variants = [],
  selectedVariant,
  onSelectVariant,
  onPoint,
  pointing = false,
  children,
}: {
  artifact: Pick<ArtifactListingEntry, 'title' | 'typeTitle' | 'path'>;
  variants?: readonly string[];
  selectedVariant?: string | undefined;
  onSelectVariant?: ((variant: string) => void) | undefined;
  /** The view takes pointing: what the user points at goes to the artifact's composer. */
  onPoint?: (() => void) | undefined;
  pointing?: boolean;
  /** The artifact's actions (its app's, publish, close). */
  children?: ReactNode;
}) {
  return (
    <header className="flex min-w-0 items-center gap-2 px-4 py-2.5">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-control text-foreground/80">
        <FileBox aria-hidden className="size-3.5" />
      </span>
      <h2
        className="type-ui min-w-0 flex-1 truncate font-semibold"
        title={`${artifact.typeTitle} · ${artifact.path}`}
      >
        {artifact.title}
      </h2>
      {variants.length > 0 && (
        <fieldset
          aria-label="Variants"
          className="m-0 flex shrink-0 items-center gap-1 border-0 p-0"
        >
          {variants.map((variant) => {
            const selected = variant === (selectedVariant ?? variants[0]);
            return (
              <Chip
                key={variant}
                onClick={() => onSelectVariant?.(variant)}
                selected={selected}
                size="xs"
              >
                {variant}
              </Chip>
            );
          })}
        </fieldset>
      )}
      {onPoint !== undefined && (
        <button
          aria-pressed={pointing}
          className="type-meta flex h-7 shrink-0 items-center gap-1.5 rounded-control border border-hairline-strong px-3 text-foreground max-sm:px-2 transition-colors hover:bg-accent aria-pressed:bg-accent"
          onClick={onPoint}
          type="button"
        >
          <MousePointer2 aria-hidden className="size-3.5" />
          {/* A phone's header keeps its title: the pointer alone, still named. */}
          <span className="max-sm:sr-only">Point at an element</span>
        </button>
      )}
      {children}
    </header>
  );
}
